import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Paper, Stack, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { buildEventsApiUrl } from '../config/api';
import { getAuthUserId } from '../utils/authUser';
import { DEFAULT_RUNTIME_SETTINGS, type RuntimeSettings } from '../services/runtimeSettings';
import { isSiteAdminUser } from '../config/admin';
import { buildAuthHeaders } from '../utils/authFetch';

const FIELD_META: Record<keyof RuntimeSettings['eventDayPolling'], { label: string; help: string; min: number; max: number }> = {
  participantWaitingPollMs: {
    label: 'Participant waiting poll interval',
    help: 'How often to refresh while a checked-in participant is waiting for assignment.',
    min: 500,
    max: 60000,
  },
  participantAssignedPollMs: {
    label: 'Participant assigned/staged poll interval',
    help: 'How often to refresh once a participant has an assigned table or staged session.',
    min: 500,
    max: 60000,
  },
  participantActivePollMs: {
    label: 'Participant active trade poll interval',
    help: 'Fast refresh during a live handoff at the table.',
    min: 250,
    max: 30000,
  },
  participantIdlePollMs: {
    label: 'Participant idle poll interval',
    help: 'Fallback refresh interval outside active event-day flow.',
    min: 1000,
    max: 120000,
  },
  adminReadyPollMs: {
    label: 'Admin ready-group poll interval',
    help: 'Refresh while groups are waiting to be launched into sessions.',
    min: 500,
    max: 60000,
  },
  adminActivePollMs: {
    label: 'Admin active-session poll interval',
    help: 'Fast refresh while trade sessions are active or staged.',
    min: 250,
    max: 30000,
  },
  adminIdlePollMs: {
    label: 'Admin idle poll interval',
    help: 'Fallback refresh interval for the admin trade-day view.',
    min: 1000,
    max: 120000,
  },
};

const SiteAdminRuntimeSettingsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = getAuthUserId(user as any);
  const isAllowed = isSiteAdminUser(user as any);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [settings, setSettings] = useState<RuntimeSettings>(DEFAULT_RUNTIME_SETTINGS);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(buildEventsApiUrl('/api/runtime-settings'), {
          headers: buildAuthHeaders(user),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load runtime settings');
        setSettings(data.settings || DEFAULT_RUNTIME_SETTINGS);
      } catch (err: any) {
        setError(err.message || 'Failed to load runtime settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const updateField = (field: keyof RuntimeSettings['eventDayPolling'], value: string) => {
    const meta = FIELD_META[field];
    const numeric = Number(value || 0);
    const clamped = Math.min(meta.max, Math.max(meta.min, numeric));
    setSettings((current) => ({
      ...current,
      eventDayPolling: {
        ...current.eventDayPolling,
        [field]: clamped,
      },
    }));
  };

  const resetToDefaults = () => {
    setSettings(DEFAULT_RUNTIME_SETTINGS);
    setSuccess(null);
    setError(null);
  };

  const groupedFields = useMemo(() => ({
    participant: [
      'participantWaitingPollMs',
      'participantAssignedPollMs',
      'participantActivePollMs',
      'participantIdlePollMs',
    ] as (keyof RuntimeSettings['eventDayPolling'])[],
    admin: [
      'adminReadyPollMs',
      'adminActivePollMs',
      'adminIdlePollMs',
    ] as (keyof RuntimeSettings['eventDayPolling'])[],
  }), []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const response = await fetch(buildEventsApiUrl('/api/runtime-settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({
          userId,
          eventDayPolling: settings.eventDayPolling,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save runtime settings');
      setSettings(data.settings || settings);
      setSuccess('Runtime settings saved.');
    } catch (err: any) {
      setError(err.message || 'Failed to save runtime settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Site Admin Runtime Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            First live-backed site-admin screen. These values control event-day polling behavior for participant and admin views.
          </Typography>

          {!isAllowed && (
            <Alert severity="warning">
              You are signed in as <strong>{userId || 'unknown'}</strong>. Only site admins can save runtime settings.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Participant polling</Typography>
                  {groupedFields.participant.map((field) => {
                    const meta = FIELD_META[field];
                    return (
                      <TextField
                        key={field}
                        type="number"
                        label={meta.label}
                        helperText={`${meta.help} Min ${meta.min} ms · Max ${meta.max} ms`}
                        value={settings.eventDayPolling[field]}
                        onChange={(e) => updateField(field, e.target.value)}
                        fullWidth
                        disabled={!isAllowed || saving}
                      />
                    );
                  })}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Admin polling</Typography>
                  {groupedFields.admin.map((field) => {
                    const meta = FIELD_META[field];
                    return (
                      <TextField
                        key={field}
                        type="number"
                        label={meta.label}
                        helperText={`${meta.help} Min ${meta.min} ms · Max ${meta.max} ms`}
                        value={settings.eventDayPolling[field]}
                        onChange={(e) => updateField(field, e.target.value)}
                        fullWidth
                        disabled={!isAllowed || saving}
                      />
                    );
                  })}
                </Stack>
              </Paper>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" onClick={handleSave} disabled={!isAllowed || saving}>
                  {saving ? 'Saving...' : 'Save Runtime Settings'}
                </Button>
                <Button variant="outlined" onClick={resetToDefaults} disabled={!isAllowed || saving}>
                  Reset to Defaults
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};

export default SiteAdminRuntimeSettingsPage;
